import { useEffect, useRef, useState } from "react";
import type {
  KakaoCustomOverlay,
  KakaoLatLng,
  KakaoMap,
  KakaoMarker,
  KakaoMarkerClusterer,
  KakaoNamespace,
} from "@/lib/kakao";
import type { LatLon, MapBounds, MapMarker } from "@/lib/types";
import { contrastText, validColor } from "@/lib/color";

const CLUSTER_THRESHOLD = 100;

interface Props {
  kakao: KakaoNamespace | null;
  markers: MapMarker[];
  center: LatLon;
  level: number;
  colorMap: Record<string, string>;
  selectedNo: number | null;
  myLocation: LatLon | null;
  loading: boolean;
  sdkError: string | null;
  fitBounds: boolean;
  onSelect: (no: number) => void;
  onResearch: (bounds: MapBounds) => void;
  onMapTouch: () => void;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

function resolveCate(m: MapMarker): string {
  return String(m.cate_name || m.cate || "기타").trim();
}

function resolveColor(m: MapMarker, colorMap: Record<string, string>): string {
  return (
    validColor(String(m.color ?? "")) ||
    validColor(colorMap[resolveCate(m)]) ||
    "#17408B"
  );
}

interface OverlayEntry {
  overlay: KakaoCustomOverlay;
  el: HTMLElement;
}

export default function BizMapCanvas({
  kakao,
  markers,
  center,
  level,
  colorMap,
  selectedNo,
  myLocation,
  loading,
  sdkError,
  fitBounds,
  onSelect,
  onResearch,
  onMapTouch,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<KakaoMap | null>(null);
  const overlaysRef = useRef<Map<number, OverlayEntry>>(new Map());
  const clustererRef = useRef<KakaoMarkerClusterer | null>(null);
  const plainMarkersRef = useRef<KakaoMarker[]>([]);
  const myLocOverlayRef = useRef<KakaoCustomOverlay | null>(null);
  const boundsRef = useRef<MapBounds | null>(null);
  const lastSearchRef = useRef<string>("");
  const initializedRef = useRef(false);
  const suppressResearchRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const onTouchRef = useRef(onMapTouch);

  const [showResearch, setShowResearch] = useState(false);

  onSelectRef.current = onSelect;
  onTouchRef.current = onMapTouch;

  // 지도 초기화 (SDK 준비 완료 후 1회)
  useEffect(() => {
    if (!kakao || !containerRef.current || mapRef.current) return;

    const map = new kakao.maps.Map(containerRef.current, {
      center: new kakao.maps.LatLng(center.lat, center.lon),
      level: level || 6,
    });
    mapRef.current = map;

    // 우측 확대/축소 컨트롤
    map.addControl(new kakao.maps.ZoomControl(), kakao.maps.ControlPosition.RIGHT);

    const readBounds = (): MapBounds | null => {
      const b = map.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      return {
        sw_lat: Number(sw.getLat().toFixed(6)),
        sw_lon: Number(sw.getLng().toFixed(6)),
        ne_lat: Number(ne.getLat().toFixed(6)),
        ne_lon: Number(ne.getLng().toFixed(6)),
      };
    };

    const handleIdle = () => {
      const nb = readBounds();
      if (!nb) return;
      boundsRef.current = nb;
      const key = JSON.stringify(nb);
      if (!initializedRef.current || suppressResearchRef.current) {
        initializedRef.current = true;
        suppressResearchRef.current = false;
        lastSearchRef.current = key;
        return;
      }
      if (key !== lastSearchRef.current) setShowResearch(true);
    };

    const handleDragStart = () => onTouchRef.current();

    kakao.maps.event.addListener(map, "idle", handleIdle);
    kakao.maps.event.addListener(map, "dragstart", handleDragStart);
    kakao.maps.event.addListener(map, "zoomstart", handleDragStart);

    // 지도 터치 시 모바일 바텀시트 접기
    const container = containerRef.current;
    const handleTouchStart = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest(".biz-marker")) return;
      onTouchRef.current();
    };
    container.addEventListener("touchstart", handleTouchStart, { passive: true });

    const handleResize = () => map.relayout();
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("resize", handleResize);
    };
    // center/level 은 최초 생성에만 사용
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kakao]);

  // 중심 좌표가 바뀌면(예: 내 위치) 지도 이동
  useEffect(() => {
    const map = mapRef.current;
    if (!kakao || !map) return;
    map.setCenter(new kakao.maps.LatLng(center.lat, center.lon));
  }, [kakao, center.lat, center.lon]);

  // 마커 렌더링 (100건 초과 시 클러스터링)
  useEffect(() => {
    const map = mapRef.current;
    if (!kakao || !map) return;

    // 정리
    overlaysRef.current.forEach((entry) => entry.overlay.setMap(null));
    overlaysRef.current.clear();
    clustererRef.current?.clear();
    clustererRef.current = null;
    plainMarkersRef.current = [];

    const valid = markers.filter((m) => {
      const lat = Number(m.lat);
      const lon = Number(m.lon);
      return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
    });

    if (valid.length > CLUSTER_THRESHOLD) {
      const clusterer = new kakao.maps.MarkerClusterer({
        map,
        averageCenter: true,
        minLevel: 6,
        disableClickZoom: false,
      });
      const list: KakaoMarker[] = valid.map((m) => {
        const marker = new kakao.maps.Marker({
          position: new kakao.maps.LatLng(Number(m.lat), Number(m.lon)),
        });
        kakao.maps.event.addListener(marker, "click", () => onSelectRef.current(m.no));
        return marker;
      });
      clusterer.addMarkers(list);
      clustererRef.current = clusterer;
      plainMarkersRef.current = list;
    } else {
      valid.forEach((m) => {
        const cate = resolveCate(m);
        const color = resolveColor(m, colorMap);
        const shortCate = cate.slice(0, 2);
        const bizName = String(m.biz_name || "사업장").trim();

        const el = document.createElement("div");
        el.className = "biz-marker";
        el.style.setProperty("--cate-color", color);
        el.innerHTML = `
          <div class="biz-marker__body">
            <span class="biz-marker__cate" style="color:${contrastText(color)}">${escapeHtml(shortCate)}</span>
            <span class="biz-marker__name">${escapeHtml(truncate(bizName, 10))}</span>
          </div>
          <span class="biz-marker__tail"></span>
        `;
        el.addEventListener("click", () => onSelectRef.current(m.no));

        const overlay = new kakao.maps.CustomOverlay({
          position: new kakao.maps.LatLng(Number(m.lat), Number(m.lon)),
          content: el,
          xAnchor: 0.5,
          yAnchor: 1,
          zIndex: 1,
          clickable: true,
        });
        overlay.setMap(map);
        overlaysRef.current.set(m.no, { overlay, el });
      });
    }

    setShowResearch(false);

    // 데이터를 다 그린 뒤 전체가 보이도록 범위 맞춤
    if (fitBounds && valid.length > 0) {
      const lats = valid.map((m) => Number(m.lat));
      const lons = valid.map((m) => Number(m.lon));
      const sw = new kakao.maps.LatLng(Math.min(...lats), Math.min(...lons));
      const ne = new kakao.maps.LatLng(Math.max(...lats), Math.max(...lons));
      const bounds = new kakao.maps.LatLngBounds(sw as KakaoLatLng, ne as KakaoLatLng);
      suppressResearchRef.current = true;
      map.setBounds(bounds);
    }
  }, [kakao, markers, colorMap, fitBounds]);

  // 내 위치 파란 원형 마커
  useEffect(() => {
    const map = mapRef.current;
    if (!kakao || !map) return;

    if (myLocOverlayRef.current) {
      myLocOverlayRef.current.setMap(null);
      myLocOverlayRef.current = null;
    }
    if (!myLocation) return;

    const el = document.createElement("div");
    el.className = "biz-myloc";
    el.innerHTML = `<span class="biz-myloc__pulse"></span><span class="biz-myloc__dot"></span>`;

    const overlay = new kakao.maps.CustomOverlay({
      position: new kakao.maps.LatLng(myLocation.lat, myLocation.lon),
      content: el,
      xAnchor: 0.5,
      yAnchor: 0.5,
      zIndex: 40,
      clickable: false,
    });
    overlay.setMap(map);
    myLocOverlayRef.current = overlay;
  }, [kakao, myLocation]);

  // 선택된 마커 강조 + 지도 이동
  useEffect(() => {
    if (!kakao || !mapRef.current) return;

    overlaysRef.current.forEach((entry, no) => {
      const selected = no === selectedNo;
      entry.el.classList.toggle("is-selected", selected);
      entry.overlay.setZIndex(selected ? 20 : 1);
    });

    if (selectedNo == null) return;
    const target = markers.find((m) => m.no === selectedNo);
    if (!target) return;
    const lat = Number(target.lat);
    const lon = Number(target.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    const map = mapRef.current;
    const currentLevel = map.getLevel();
    if (currentLevel > 5) map.setLevel(4, { animate: true });
    map.panTo(new kakao.maps.LatLng(lat, lon));
  }, [kakao, selectedNo, markers]);

  const handleResearchClick = () => {
    if (!boundsRef.current) return;
    lastSearchRef.current = JSON.stringify(boundsRef.current);
    setShowResearch(false);
    onResearch(boundsRef.current);
  };

  return (
    <div className="relative h-full w-full bg-background-100">
      <div ref={containerRef} className="h-full w-full" aria-label="회원 사업장 지도" />

      {/* 재검색 버튼 */}
      {kakao && showResearch && !sdkError && (
        <button
          type="button"
          onClick={handleResearchClick}
          className="absolute left-1/2 top-4 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm font-semibold text-primary-700 shadow-md hover:bg-background-100 transition-colors"
        >
          <i className="ri-refresh-line mr-1.5 align-middle" aria-hidden="true"></i>
          이 지역 재검색
        </button>
      )}

      {/* 로딩 오버레이 */}
      {loading && (
        <div className="absolute right-4 top-4 z-10 flex items-center gap-2 rounded-full bg-background-50/95 px-3.5 py-2 text-xs font-medium text-foreground-600 shadow-sm">
          <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
          불러오는 중
        </div>
      )}

      {/* SDK 오류 */}
      {sdkError && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background-100 px-6 text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-map-2-line text-2xl" aria-hidden="true"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">지도를 표시할 수 없습니다</p>
          <p className="max-w-xs text-xs text-foreground-500">{sdkError}</p>
        </div>
      )}

      {/* SDK 로딩 */}
      {!kakao && !sdkError && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background-100">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" aria-hidden="true"></i>
          <p className="text-sm text-foreground-500">지도를 준비하고 있습니다</p>
        </div>
      )}
    </div>
  );
}